import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined'
import CloseIcon from '@mui/icons-material/Close'
import InfoIcon from '@mui/icons-material/Info'
import {
  Box,
  Button,
  Drawer,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
  Typography
} from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { AttendanceStatusButton } from 'Pages/Attendance/AttendanceStatusButton'
import { AttendanceStatusChip } from 'Pages/Attendance/AttendanceStatusChip'
import { type AttendanceCellDto, type AttendanceDateColumnDto, type AttendanceRecordDraftUpdate, type AttendanceRecordStatus, type AttendanceRosterRowDto } from 'Pages/Attendance/types'

const attendanceFontFamily = 'Quicksand, sans-serif'
const purple = '#7136BC'
const textPrimary = '#10091B'
const textSecondary = '#595959'
const statuses: AttendanceRecordStatus[] = ['Present', 'Tardy', 'Absent']

export function AttendanceRecordDrawer (props: {
  open: boolean
  dates: AttendanceDateColumnDto[]
  row: AttendanceRosterRowDto | null
  savingCells?: Set<string>
  onClose: () => void
  onSaveChanges: (row: AttendanceRosterRowDto, updates: AttendanceRecordDraftUpdate[]) => void
}): JSX.Element {
  const [statusMenu, setStatusMenu] = useState<{ anchor: HTMLElement, cell: AttendanceCellDto } | null>(null)
  const [notesByDate, setNotesByDate] = useState<Record<string, string>>({})
  const [statusesByDate, setStatusesByDate] = useState<Record<string, AttendanceRecordStatus | ''>>({})

  useEffect(() => {
    if (!props.open) return
    setStatusMenu(null)
    setNotesByDate(Object.fromEntries((props.row?.cells ?? []).map(cell => [dateKey(cell.attendanceDate), cell.note ?? ''])))
    setStatusesByDate(Object.fromEntries((props.row?.cells ?? []).map(cell => [dateKey(cell.attendanceDate), cell.status ?? ''])))
  }, [props.open, props.row])

  const activeDays = useMemo(() => {
    return props.dates
      .filter(date => date.isInSession)
      .map(date => ({ date, cell: cellForDate(props.row, date.attendanceDate) }))
  }, [props.dates, props.row])

  const changedRecords = useMemo(() => {
    if (props.row == null) return []

    return props.row.cells
      .map(cell => {
        const key = dateKey(cell.attendanceDate)
        const status = statusesByDate[key]
        if (status == null) return null

        if (status === '') {
          if (!cell.canEdit || cell.status == null) return null
          return { cell, status: null }
        }

        const statusChanged = cell.canEdit && status !== (cell.status ?? '')
        const note = status === 'Absent' && cell.canAddNote ? normalizedNote(notesByDate[key] ?? '') : undefined
        const noteChanged = note !== undefined && normalizedNote(cell.note ?? '') !== note
        if (!statusChanged && !noteChanged) return null

        const update: AttendanceRecordDraftUpdate = { cell, status }
        if (note !== undefined) {
          update.note = note
        }
        return update
      })
      .filter((update): update is AttendanceRecordDraftUpdate => update != null)
  }, [notesByDate, props.row, statusesByDate])

  const saving = activeDays.some(day => day.cell != null && props.savingCells?.has(cellKey(props.row?.newEnrollmentId ?? 0, day.cell.attendanceDate)) === true)
  const canSave = props.row != null && changedRecords.length > 0 && !saving
  const readOnly = props.row != null && !props.row.cells.some(cell => cell.canEdit || cell.canAddNote)

  return (
    <Drawer
      anchor="right"
      open={props.open}
      onClose={props.onClose}
      sx={{ zIndex: 1202 }}
      PaperProps={{
        sx: {
          borderRadius: { xs: 0, sm: '8px' },
          bottom: { xs: 0, sm: 24 },
          boxShadow: '0px 4px 24px 4px rgba(0,0,0,0.06)',
          boxSizing: 'border-box',
          height: { xs: '100%', sm: 'auto' },
          maxWidth: '100vw',
          px: { xs: 2, sm: 4 },
          py: { xs: 2, sm: 4 },
          right: { xs: 0, sm: 24 },
          top: { xs: 0, sm: 24 },
          width: { xs: '100vw', sm: 514 }
        }
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box sx={{ alignItems: 'flex-start', display: 'flex', justifyContent: 'space-between' }}>
          <Typography component="h2" sx={{ color: textPrimary, fontFamily: attendanceFontFamily, fontSize: 20, fontWeight: 600 }}>
            Attendance Record for {props.row?.childName ?? 'Student'}
          </Typography>
          <IconButton aria-label="Close attendance record" onClick={props.onClose} sx={{ color: '#514F53', height: 32, width: 32 }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ alignItems: 'center', borderBottom: '1px solid #B2B2B4', display: 'flex', gap: 0.5, mt: 2, pb: 1 }}>
          <Typography sx={{ color: textPrimary, fontFamily: attendanceFontFamily, fontSize: 14, fontWeight: 700 }}>
            Weekly Attendance
          </Typography>
          {!readOnly && (
            <Tooltip title="Absence notes can be edited during this week or last week. Older blank notes can be added once.">
              <InfoIcon sx={{ color: '#2C2C2C', fontSize: 14 }} />
            </Tooltip>
          )}
        </Box>

        <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', py: 1.25 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            {activeDays.length === 0
              ? (
                <Typography sx={{ color: textSecondary, fontFamily: attendanceFontFamily, fontSize: 14 }}>
                  No active session days are available for this week.
                </Typography>
                )
              : activeDays.map(day => (
                <WeeklyAttendanceItem
                  key={day.date.attendanceDate}
                  cell={day.cell}
                  date={day.date}
                  note={day.cell == null ? '' : (notesByDate[dateKey(day.cell.attendanceDate)] ?? '')}
                  readOnly={readOnly}
                  saving={day.cell != null && props.savingCells?.has(cellKey(props.row?.newEnrollmentId ?? 0, day.cell.attendanceDate)) === true}
                  status={day.cell == null ? '' : (statusesByDate[dateKey(day.cell.attendanceDate)] ?? '')}
                  onNoteChange={(value) => {
                    const cell = day.cell
                    if (cell == null) return
                    setNotesByDate(current => ({ ...current, [dateKey(cell.attendanceDate)]: value }))
                  }}
                  onStatusMenuOpen={(anchor) => {
                    const cell = day.cell
                    if (cell == null) return
                    setStatusMenu({ anchor, cell })
                  }}
                />
              ))}
          </Box>
        </Box>

        <Box sx={{ alignItems: 'center', borderTop: '1px solid #E6E6E6', display: 'flex', justifyContent: readOnly ? 'flex-start' : 'space-between', mx: { xs: -2, sm: -4 }, px: { xs: 2, sm: 4 }, py: 2 }}>
          <Button
            variant="outlined"
            onClick={props.onClose}
            sx={drawerButtonSx({ backgroundColor: '#FFFFFF', color: purple, borderColor: purple })}
          >
            Close
          </Button>
          {!readOnly && (
            <Button
              variant="contained"
              disabled={!canSave}
              onClick={() => {
                if (props.row == null) return
                props.onSaveChanges(props.row, changedRecords)
              }}
              sx={drawerButtonSx({ backgroundColor: purple, color: '#FFFFFF' })}
            >
              Save Changes
            </Button>
          )}
        </Box>
      </Box>
      <Menu anchorEl={statusMenu?.anchor ?? null} open={statusMenu != null} onClose={() => { setStatusMenu(null) }}>
        {statuses.map(status => (
          <MenuItem
            key={status}
            disabled={statusMenu == null || statusesByDate[dateKey(statusMenu.cell.attendanceDate)] === status}
            sx={menuItemSx}
            onClick={() => {
              if (statusMenu == null) return
              setStatusesByDate(current => ({ ...current, [dateKey(statusMenu.cell.attendanceDate)]: status }))
              setStatusMenu(null)
            }}
          >
            Mark {status}
          </MenuItem>
        ))}
        {statusMenu?.cell.status != null && statusMenu.cell.canEdit && (
          <MenuItem
            sx={menuItemSx}
            onClick={() => {
              if (statusMenu == null) return
              setStatusesByDate(current => ({ ...current, [dateKey(statusMenu.cell.attendanceDate)]: '' }))
              setStatusMenu(null)
            }}
          >
            Clear Response
          </MenuItem>
        )}
      </Menu>
    </Drawer>
  )
}

function WeeklyAttendanceItem (props: {
  cell: AttendanceCellDto | undefined
  date: AttendanceDateColumnDto
  note: string
  readOnly: boolean
  saving: boolean
  status: AttendanceRecordStatus | ''
  onNoteChange: (value: string) => void
  onStatusMenuOpen: (anchor: HTMLElement) => void
}): JSX.Element {
  const showNoteEditor = props.status === 'Absent'
  const noteEditable = props.cell?.canAddNote === true && !props.saving
  const readOnlyNote = props.readOnly && props.status === 'Absent' ? props.note.trim() : ''

  return (
    <Box sx={{ border: '1px solid rgba(89,89,89,0.28)', borderRadius: '8px', display: 'flex', flexDirection: 'column', gap: 0.75, p: 2 }}>
      <Box sx={{ alignItems: 'center', display: 'flex', gap: 1, justifyContent: 'space-between' }}>
        <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
          <CalendarTodayOutlinedIcon sx={{ color: '#2C2C2C', fontSize: 20 }} />
          <Typography sx={{ color: '#2C2C2C', fontFamily: attendanceFontFamily, fontSize: 14, fontWeight: 600 }}>
            {fullDateLabel(props.date.attendanceDate)}
          </Typography>
        </Box>
        {props.cell == null
          ? <AttendanceStatusChip status="NotScheduled" />
          : props.cell.canEdit
            ? (
              <Box sx={{ minWidth: 116 }}>
                <AttendanceStatusButton
                  ariaLabel={`Attendance status for ${fullDateLabel(props.date.attendanceDate)}`}
                  cell={{
                    ...props.cell,
                    status: props.status === '' ? null : props.status
                  }}
                  disabled={props.saving}
                  saving={props.saving}
                  onClick={event => { props.onStatusMenuOpen(event.currentTarget) }}
                />
              </Box>
              )
            : <AttendanceStatusChip status={props.status === '' ? 'Empty' : props.status} disabled inactive={props.status !== ''} />}
      </Box>
      {showNoteEditor && !props.readOnly && (
        <TextField
          multiline
          minRows={3}
          placeholder="Add optional notes or reason for absence..."
          value={props.note}
          disabled={!noteEditable}
          onChange={event => { props.onNoteChange(event.target.value) }}
          inputProps={{ 'aria-label': `Attendance note for ${fullDateLabel(props.date.attendanceDate)}` }}
          sx={{
            '& .MuiOutlinedInput-root': {
              alignItems: 'flex-start',
              borderRadius: '8px',
              fontFamily: 'Inter, sans-serif',
              fontSize: 14,
              minHeight: 84
            }
          }}
        />
      )}
      {readOnlyNote !== '' && (
        <Typography sx={{ color: textSecondary, fontFamily: 'Inter, sans-serif', fontSize: 14, lineHeight: 1.4 }}>
          {readOnlyNote}
        </Typography>
      )}
    </Box>
  )
}

function drawerButtonSx (tokens: { backgroundColor: string, color: string, borderColor?: string }): object {
  return {
    backgroundColor: tokens.backgroundColor,
    borderColor: tokens.borderColor,
    borderRadius: '24px',
    boxShadow: `0px 8px 10px -4px ${tokens.color === '#FFFFFF' ? 'rgba(113,54,188,0.2)' : 'rgba(113,54,188,0.12)'}`,
    color: tokens.color,
    fontFamily: attendanceFontFamily,
    fontSize: 14,
    fontWeight: 600,
    px: 3.5,
    py: 1.25,
    textTransform: 'none',
    '&:hover': {
      backgroundColor: tokens.backgroundColor,
      borderColor: tokens.borderColor
    }
  }
}

const menuItemSx = {
  color: textPrimary,
  fontFamily: attendanceFontFamily,
  fontSize: '0.875rem',
  fontWeight: 500,
  minHeight: 36
}

function fullDateLabel (value: string): string {
  const [year, month, day] = value.substring(0, 10).split('-').map(Number)
  return new Date(year, month - 1, day).toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  })
}

function cellForDate (row: AttendanceRosterRowDto | null, attendanceDate: string): AttendanceCellDto | undefined {
  return row?.cells.find(cell => dateKey(cell.attendanceDate) === dateKey(attendanceDate))
}

function dateKey (value: string): string {
  return value.substring(0, 10)
}

function cellKey (newEnrollmentId: number, attendanceDate: string): string {
  return `${newEnrollmentId}-${dateKey(attendanceDate)}`
}

function normalizedNote (value: string): string | null {
  const trimmed = value.trim()
  return trimmed === '' ? null : trimmed
}
